import React, { useState, useEffect } from "react";
import {
    X, Receipt, DollarSign, Clock, CheckCircle,
    ArrowRightLeft, AlertCircle, ShoppingBag, Plus
} from "lucide-react";
import { Button } from "./ui/button";
import { restaurantPortalAPI } from "../services/restaurantApiClient";
import { useLanguage } from "../contexts/LanguageContext";
import { toast } from "react-hot-toast";
import LoadingSpinner from "./ui/dashboard/LoadingSpinner";

interface TableOrdersDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    tableId: string;
    tableNumber: string;
    restaurantId: string;
    onTableClosed: () => void;
    onAddItems?: () => void;
}

export default function TableOrdersDrawer({
    isOpen,
    onClose,
    tableId,
    tableNumber,
    restaurantId,
    onTableClosed,
    onAddItems
}: TableOrdersDrawerProps) {
    const { t, isRTL } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any>(null);

    // Actions state
    const [actionLoading, setActionLoading] = useState(false);
    const [showPayment, setShowPayment] = useState(false);
    const [showMove, setShowMove] = useState(false);
    const [availableTables, setAvailableTables] = useState<any[]>([]);
    const [selectedTargetTable, setSelectedTargetTable] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('cash');

    useEffect(() => {
        if (isOpen && tableId) {
            fetchOrders();
            // Reset views
            setShowPayment(false);
            setShowMove(false);
        }
    }, [isOpen, tableId]);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const res = await restaurantPortalAPI.getTableOrders(tableId);
            setData(res.data);
        } catch (error) {
            console.error("Failed to fetch table orders", error);
            toast.error(t("failedToLoadOrders") || "Failed to load orders");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseTable = async () => {
        setActionLoading(true);
        try {
            await restaurantPortalAPI.closeTable(tableId, { paymentMethod });
            toast.success(t("tableClosedSuccessfully") || "Table closed and payment confirmed");
            onTableClosed(); // Refresh tables list
            onClose();
        } catch (error: any) {
            toast.error(error.response?.data?.message || t("failedToCloseTable") || "Failed to close table");
        } finally {
            setActionLoading(false);
        }
    };

    const handleLoadTablesForMove = async () => {
        setShowMove(true);
        setShowPayment(false);
        try {
            const res = await restaurantPortalAPI.getTables(restaurantId);
            setAvailableTables(res.data.filter((t: any) => t.status === "available"));
        } catch (error) {
            toast.error("Failed to load available tables");
        }
    };

    const handleMoveTable = async () => {
        if (!selectedTargetTable) return toast.error("Select a target table");

        setActionLoading(true);
        try {
            await restaurantPortalAPI.moveTableOrders(tableId, { targetTableId: selectedTargetTable });
            toast.success("Table moved successfully");
            onTableClosed();
            onClose();
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Failed to move table");
        } finally {
            setActionLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div
                className="fixed inset-0 bg-black/50 z-40 transition-opacity"
                onClick={onClose}
            />
            <div
                className={`fixed top-0 bottom-0 ${isRTL ? 'left-0' : 'right-0'} w-full max-w-md bg-white dark:bg-gray-800 shadow-2xl z-50 transform transition-transform flex flex-col`}
                dir={isRTL ? 'rtl' : 'ltr'}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            {t("table")} {tableNumber}
                            {data?.tableStatus === 'payment_pending' && (
                                <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full flex items-center gap-1">
                                    <AlertCircle size={12} /> Pending Payment
                                </span>
                            )}
                        </h2>
                        {data?.sessionStartedAt && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                                <Clock size={12} />
                                Occupied since {new Date(data.sessionStartedAt).toLocaleTimeString()}
                            </p>
                        )}
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <LoadingSpinner text="Loading orders..." />
                    ) : data?.orders?.length > 0 ? (
                        <div className="space-y-4">
                            {data.orders.map((order: any) => (
                                <div key={order._id} className="p-3 border rounded-xl border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="font-semibold text-gray-900 dark:text-white">#{order.orderNumber}</span>
                                        <span className="text-sm font-medium text-gray-500">${order.pricing?.total || 0}</span>
                                    </div>
                                    <div className="space-y-1">
                                        {order.items.map((item: any, idx: number) => (
                                            <div key={idx} className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                                                <span>{item.quantity}x {item.foodItem?.name || 'Item'}</span>
                                                <span>${((item.price || 0) * item.quantity).toFixed(2)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-48 text-gray-500">
                            <ShoppingBag size={48} className="mb-4 opacity-50" />
                            <p>No active orders for this table session.</p>
                        </div>
                    )}
                </div>

                {/* Footer actions */}
                {!loading && data && (
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                        {/* Grand Total */}
                        <div className="flex justify-between items-center mb-4">
                            <span className="text-gray-600 dark:text-gray-400">Total ({data.summary.orderCount} orders)</span>
                            <span className="text-2xl font-bold text-red-600">${data.summary.total.toFixed(2)}</span>
                        </div>

                        {/* Views (Move or Pay) */}
                        {showPayment ? (
                            <div className="space-y-4 mb-4 p-4 border rounded-xl bg-white dark:bg-gray-800">
                                <h3 className="font-medium text-gray-900 dark:text-white">Confirm Payment</h3>
                                <div className="flex gap-2">
                                    {(() => {
                                        const availableMethods = data?.enabledPaymentMethods?.length > 0
                                            ? data.enabledPaymentMethods
                                            : ['cash'];

                                        // Auto-select first if current isn't valid
                                        if (!availableMethods.includes(paymentMethod)) {
                                            setTimeout(() => setPaymentMethod(availableMethods[0]), 0);
                                        }

                                        return availableMethods.map((method: string) => (
                                            <button
                                                key={method}
                                                onClick={() => setPaymentMethod(method)}
                                                className={`flex-1 py-2 rounded-lg border text-sm capitalize ${paymentMethod === method
                                                    ? 'border-red-500 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                                                    : 'border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-400'
                                                    }`}
                                            >
                                                {method.replace('_', ' ')}
                                            </button>
                                        ));
                                    })()}
                                </div>
                                <div className="flex gap-2 pt-2">
                                    <Button variant="outline" className="flex-1" onClick={() => setShowPayment(false)}>Cancel</Button>
                                    <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleCloseTable} disabled={actionLoading}>
                                        {actionLoading ? "Confirming..." : "Confirm & Close"}
                                    </Button>
                                </div>
                            </div>
                        ) : showMove ? (
                            <div className="space-y-4 mb-4 p-4 border rounded-xl bg-white dark:bg-gray-800">
                                <h3 className="font-medium text-gray-900 dark:text-white">Move to Table</h3>
                                <select
                                    className="w-full p-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    value={selectedTargetTable}
                                    onChange={(e) => setSelectedTargetTable(e.target.value)}
                                >
                                    <option value="">Select available table...</option>
                                    {availableTables.map(t => (
                                        <option key={t._id} value={t._id}>Table {t.tableNumber} ({t.capacity} seats)</option>
                                    ))}
                                </select>
                                <div className="flex gap-2 pt-2">
                                    <Button variant="outline" className="flex-1" onClick={() => setShowMove(false)}>Cancel</Button>
                                    <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" onClick={handleMoveTable} disabled={actionLoading || !selectedTargetTable}>
                                        {actionLoading ? "Moving..." : "Move Orders"}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2">
                                <Button
                                    onClick={() => onAddItems && onAddItems()}
                                    className="w-full h-12 rounded-xl text-white bg-indigo-600 hover:bg-indigo-700"
                                >
                                    <Plus className={`${isRTL ? 'ml-2' : 'mr-2'} w-4 h-4`} />
                                    {t("addItems") || "Add Items"}
                                </Button>
                                <div className="flex gap-2">
                                    <Button
                                        onClick={handleLoadTablesForMove}
                                        variant="outline"
                                        className="flex-1 h-12 rounded-xl border-gray-300"
                                    >
                                        <ArrowRightLeft className={`${isRTL ? 'ml-2' : 'mr-2'} w-4 h-4`} />
                                        {t("moveTable") || "Move Table"}
                                    </Button>
                                    <Button
                                        onClick={() => setShowPayment(true)}
                                        className={`flex-1 h-12 rounded-xl text-white ${data.tableStatus === 'payment_pending'
                                            ? 'bg-yellow-500 hover:bg-yellow-600'
                                            : 'bg-green-600 hover:bg-green-700'
                                            }`}
                                    >
                                        <CheckCircle className={`${isRTL ? 'ml-2' : 'mr-2'} w-4 h-4`} />
                                        {data.tableStatus === 'payment_pending' ? (t("confirmPayment") || 'Confirm Payment') : (t("closeAndPay") || 'Close & Pay')}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}
